import { RAI_SSO_SERVICE_URL } from "../definitions.js"
import { raisso } from "../raisso.js"
import { RaissoError, ServiceResponse, errorFactory } from "../responses.js"
import { apiCall, responseValidation } from "../utils.js"
import { Device, DeviceDetail, DeviceRequest, DeviceResponse } from "./service-models.js"
import { deviceDetailSchema, deviceResponseSchema, deviceSchema } from "./utils.js"
import { R } from '@mobily/ts-belt'
import { z } from "zod"

const BASE_URL = `${RAI_SSO_SERVICE_URL}/device`

function error(e: unknown) {
  console.error('[device-controller] Error:', e);
}

class DeviceController {
  private constructor() {/* empty */}

  private static _instance: DeviceController
  public static get instance(): DeviceController {
    if (!this._instance) {
      this._instance = new DeviceController()
    }
    return this._instance
  }

  public async unparing(authDevice: string): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/unpairing`
    try {
      const response = await apiCall<DeviceResponse>(url, 'json', 'POST', {
        'x-auth-token': authDevice
      })
      const value = R.map(response, responseValidation(deviceResponseSchema))
      return value;
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(
        errorFactory(RaissoError,
          (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
          { cause: e }
      ))
    }
  }

  public async pairing(data: DeviceRequest): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/pairing`
    try {
      const response = await raisso.callAuthFetch<DeviceResponse>(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      return R.map(response, responseValidation(deviceResponseSchema));
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(
        errorFactory(RaissoError, e instanceof Error ? e.message : 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify(data) } })
      )
    }
  }

  public async pairingLogged(appId?: string): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/pairing/logged`
    try {
      const response = await raisso.callAuthFetch<DeviceResponse>(url, {
        method: 'POST',
        headers: appId ? { appId } : {},
      });
      return R.map(response, responseValidation(deviceResponseSchema))
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(errorFactory(RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async pairingInit(data: DeviceRequest): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/pairing/init`
    try {
      const response = await apiCall<DeviceResponse>(
        url,
        'json',
        'POST',
        { 'content-type': 'application/json' },
        JSON.stringify(data),
      )
      let resp = R.map(response, responseValidation(deviceResponseSchema));
      resp = R.mapError(resp, e => {
        if (e.context) {
          e.context.request = { url, body: JSON.stringify(data) }
        }
        return e;
      })
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(errorFactory(RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ));
    }
  }

  public async pairingCheck(data: DeviceRequest): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/pairing/check`
    try {
      const response = await apiCall<DeviceResponse>(url, 'json', 'POST', { 'content-type': 'application/json' }, JSON.stringify(data))
      let resp = R.map(response, responseValidation(deviceResponseSchema))
      resp = R.mapError(resp, e => {
        if (e.context) { e.context.request = { url, body: JSON.stringify(data) }}
        return e;
      });
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(errorFactory(RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async getAppId(data: DeviceRequest): ServiceResponse<DeviceResponse> {
    const url = `${BASE_URL}/getAppID`
    try {
      const response = await apiCall<DeviceResponse>(url, 'json', 'POST', { 'content-type': 'application/json' }, JSON.stringify(data))
      let resp = R.map(response, responseValidation(deviceResponseSchema))
      resp = R.mapError(resp, e => {
        if (e.context) { e.context.request = { url, body: JSON.stringify(data) } }
        return e
      })
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ));
    }
  }

  public async appId(appId: string): ServiceResponse<Device> {
    const url = `${BASE_URL}/${appId}`
    try {
      const response = await apiCall<Device>(url, 'json', 'GET')
      let resp = R.map(response, responseValidation(deviceSchema))
      resp = R.mapError(resp, e => {
        if (e.context) { e.context.request = { url } }
        return e
      })
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error(errorFactory(RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto', { cause: e, context: { data: appId } }))
    }
  }

  public async users(): ServiceResponse<Record<string, string[]>> {
    const url = `${BASE_URL}/users`
    try {
      const response = await raisso.callAuthFetch<Record<string, string[]>>(url, {
        method: 'GET',
      })
      const validatorFn = z.record(z.array(z.string()))
      const resp = R.map(response, responseValidation(validatorFn))
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ))
    }
  }

  public async list(): ServiceResponse<DeviceDetail[]> {
    const url = `${BASE_URL}/list`
    try {
      const response = await raisso.callAuthFetch<DeviceDetail[]>(url, {
        method: 'GET',
      })
      const validator = z.array(deviceDetailSchema);
      const resp = R.map(response, responseValidation(validator))
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { return R.Error(e) }
      error(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e }
      ));
    }
  }
}

export default DeviceController
